// Minimal IndexedDB wrapper for storing File / Blob objects across page
// reloads. localStorage doesn't accept Blobs natively (you'd have to
// base64-encode, which doubles size and saturates the 5 MB quota fast).
//
// Used by useFormDraft to persist the front / back image picks on the
// add-sample form so an accidental refresh doesn't lose the user's work.
//
// Single object store, key=string, value=Blob. Anything stringy goes in
// localStorage instead (see useFormDraft for the split).

const DB_NAME = "cc-os-form-drafts";
const STORE = "blobs";
const VERSION = 1;

let dbPromise: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  // Detached promise so we can null `dbPromise` on rejection — without
  // this, a one-off open failure would poison every subsequent IDB call
  // because the cached promise stays rejected forever.
  const p = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB unavailable in this environment"));
      return;
    }
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB open failed"));
    req.onblocked = () => reject(new Error("IndexedDB open blocked by another tab"));
  });
  // On rejection clear the cache so the next caller retries — open()
  // failures are usually transient (quota / private mode flicker).
  p.catch(() => { dbPromise = null; });
  dbPromise = p;
  return dbPromise;
}

/** Stash a Blob (typically a File from <input type=file>) under `key`.
 *  Resolves once the transaction commits — call `await` so the next
 *  refresh can read it back. */
export async function idbPutBlob(key: string, blob: Blob): Promise<void> {
  const db = await open();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(blob, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("IDB put failed"));
    tx.onabort = () => reject(tx.error ?? new Error("IDB put aborted"));
  });
}

/** Read a previously stashed Blob. Returns null if no entry exists or
 *  IndexedDB is unavailable (e.g. private-mode Safari). */
export async function idbGetBlob(key: string): Promise<Blob | null> {
  try {
    const db = await open();
    return await new Promise<Blob | null>((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => resolve((req.result as Blob | undefined) ?? null);
      req.onerror = () => reject(req.error ?? new Error("IDB get failed"));
    });
  } catch (err) {
    console.warn("idbGetBlob failed", err);
    return null;
  }
}

/** Drop every entry whose key starts with `prefix`. Used when a form's
 *  draft is cleared (submit success or explicit "discard"). */
export async function idbDeleteByPrefix(prefix: string): Promise<void> {
  try {
    const db = await open();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      const req = store.openCursor();
      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor) return;
        const k = String(cursor.key);
        if (k.startsWith(prefix)) cursor.delete();
        cursor.continue();
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("IDB cursor failed"));
    });
  } catch (err) {
    console.warn("idbDeleteByPrefix failed", err);
  }
}
